import CryptoJS from 'crypto-js';

/**
 * Validates if a key meets requirements
 * @param {string} key - The encryption key to validate
 * @returns {Object} - Contains validation result and error message if any
 */
export const validateKey = (key) => {
  if (!key || key.length < 4) {
    return {
      isValid: false,
      error: 'The key must be at least 4 characters long',
    };
  }
  return { isValid: true, error: '' };
};

/**
 * Evaluates the strength of a key
 * @param {string} key - The encryption key to evaluate
 * @returns {Object} - Contains strength level and feedback
 */
export const evaluateKeyStrength = (key) => {
  if (!key) return { level: 0, feedback: 'No key provided' };
  
  let score = 0;
  let feedback = [];
  
  // Length check
  if (key.length < 4) {
    score = 0;
    feedback.push('Key is too short');
  } else if (key.length >= 12) {
    score += 3;
  } else if (key.length >= 8) {
    score += 2;
  } else {
    score += 1;
    feedback.push('Consider a longer key (8+ characters)');
  }
  
  // Complexity checks
  if (/[A-Z]/.test(key)) score += 1;
  else feedback.push('Add uppercase letters');
  
  if (/[0-9]/.test(key)) score += 1;
  else feedback.push('Add numbers');
  
  if (/[^A-Za-z0-9]/.test(key)) score += 1;
  else feedback.push('Add special characters');
  
  // Determine level based on score
  let level;
  let strengthText;
  
  if (score < 2) {
    level = 1;
    strengthText = 'Very Weak';
  } else if (score < 3) {
    level = 2;
    strengthText = 'Weak';
  } else if (score < 4) {
    level = 3;
    strengthText = 'Moderate';
  } else if (score < 5) {
    level = 4;
    strengthText = 'Strong';
  } else {
    level = 5;
    strengthText = 'Very Strong';
  }
  
  return {
    level,
    score,
    strengthText,
    feedback: feedback.join(', ')
  };
};

/**
 * Encrypts JSON data using AES encryption
 * @param {Object} jsonData - The JSON data to encrypt
 * @param {string} key - The encryption key
 * @returns {Object} - Contains encrypted string or error
 */
export const encryptJSON = (jsonData, key) => {
  try {
    // Validate the key
    const keyValidation = validateKey(key);
    if (!keyValidation.isValid) {
      return { success: false, error: keyValidation.error };
    }

    // Convert JSON to string
    const jsonString = JSON.stringify(jsonData);
    
    // Encrypt the string
    const encrypted = CryptoJS.AES.encrypt(jsonString, key);
    if (!encrypted) {
      throw new Error('Encryption failed');
    }

    return {
      success: true,
      data: encrypted.toString(),
    };
  } catch (error) {
    console.error('JSON encryption error:', error);
    return {
      success: false,
      error: error.message || 'An error occurred during encryption',
    };
  }
};

/**
 * Decrypts encrypted JSON data using AES decryption
 * @param {string} encryptedData - The encrypted string
 * @param {string} key - The decryption key
 * @returns {Object} - Contains decrypted JSON or error
 */
export const decryptJSON = (encryptedData, key) => {
  try {
    // Validate the key
    const keyValidation = validateKey(key);
    if (!keyValidation.isValid) {
      return { success: false, error: keyValidation.error };
    }

    if (!encryptedData) {
      return { success: false, error: 'No encrypted data provided' };
    }

    // Decrypt the data
    const decrypted = CryptoJS.AES.decrypt(encryptedData, key);
    const decryptedString = decrypted.toString(CryptoJS.enc.Utf8);
    
    // Parse the JSON
    const jsonData = JSON.parse(decryptedString);
    
    return {
      success: true,
      data: jsonData,
    };
  } catch (error) {
    console.error('JSON decryption error:', error);
    return {
      success: false,
      error: 'Decryption failed. Check your key or encrypted data.',
    };
  }
};

/**
 * Generate a timestamp-based filename
 * @param {string} prefix - Prefix for the filename
 * @returns {string} - Generated filename
 */
export const generateFilename = (prefix) => {
  const now = new Date();
  const formattedDate = now
    .toISOString()
    .replace(/[-:]/g, '')
    .replace('T', '_')
    .split('.')[0];
  
  return `${prefix}_${formattedDate}.json`;
};